import React from "react";
import useTilt from "../hooks/useTilt";

const TONES = {
  light:
    "border-[#EAE2D6] bg-white shadow-[0_1px_2px_rgba(30,27,22,0.04)] hover:border-[#D8B4FE] hover:shadow-[0_34px_60px_-30px_rgba(106,13,173,0.5)]",
  glass: "mw-glass hover:shadow-[0_34px_60px_-28px_rgba(106,13,173,0.45)]",
  dark: "mw-glass-dark hover:border-white/25",
  /* Reads as open space until the pointer arrives, then a glass panel
     materialises under the content. For rails inside the dark bands, where
     four permanently bordered cards would crowd everything around them. */
  bare:
    "border-transparent bg-white/0 hover:border-white/15 hover:bg-white/[0.07] hover:shadow-[0_30px_60px_-30px_rgba(0,0,0,0.75)]",
  /* Bare on purpose: the caller draws the surface as a child layer instead.
     `glass`/`dark` reach for backdrop-filter, which is a grouping property —
     on the face it forces transform-style back to flat and collapses every
     .mw-z* layer onto it, the same way overflow: hidden does. Drawing the
     glass one level in keeps the blur and the depth. */
  plate: "border-transparent bg-transparent"
};

/**
 * A card with real depth: the wrapper owns the perspective, the face rotates
 * towards the pointer, and anything marked .mw-z1/.mw-z2/.mw-z3 inside stands
 * off the face so the rotation produces parallax between the layers rather
 * than tipping a flat picture.
 *
 * `as` lets the same card render as a button or an anchor without a nested
 * interactive element, which would break both keyboard order and hit testing.
 */
function Card3D({
  children,
  as: Tag = "div",
  tone = "light",
  lift = 6,
  max = 7,
  className = "",
  innerClassName = "",
  ...rest
}) {
  const tilt = useTilt({ max, lift });

  return (
    <div className={`mw-scene ${className}`}>
      {/* No `overflow-hidden` on this element, deliberately: any overflow
          other than visible forces transform-style back to flat, which would
          collapse the .mw-z* layers onto the card face and lose the parallax.
          The decorative layers clip themselves instead. */}
      <Tag
        ref={tilt.ref}
        onPointerMove={tilt.onPointerMove}
        onPointerLeave={tilt.onPointerLeave}
        className={`mw-3d group relative h-full w-full rounded-2xl border text-left focus:outline-none focus-visible:ring-4 focus-visible:ring-[#6A0DAD]/25 ${TONES[tone]} ${innerClassName}`}
        {...rest}
      >
        {/* Specular highlight, tracking the pointer. */}
        <span
          className="mw-specular pointer-events-none absolute inset-0 rounded-[inherit]"
          aria-hidden="true"
        />
        {/* Top-edge light catch, held inside the corner radius. */}
        <span
          className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-[#D8B4FE] to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100"
          aria-hidden="true"
        />
        {children}
      </Tag>
    </div>
  );
}

export default Card3D;
